/**
 * Weather File Cache Service
 *
 * Caches downloaded EPW files in IndexedDB for offline access
 * and faster subsequent loads.
 */

import type {
  CachedWeatherFile,
  RecentWeatherFile,
  FavoriteWeatherFile,
  WeatherLibraryPreferences,
} from '../types';
import { DEFAULT_WEATHER_LIBRARY_PREFERENCES } from '../types';

const DB_NAME = 'envelop-weather-cache';
const DB_VERSION = 3;

// Store names
const CACHE_STORE = 'cached-files';
const RECENT_STORE = 'recent-files';
const FAVORITES_STORE = 'favorites';
const PREFERENCES_STORE = 'preferences';
const DDY_CACHE_STORE = 'cached-ddy-files';
const SEARCH_HISTORY_STORE = 'search-history';

let db: IDBDatabase | null = null;

/**
 * Open the IndexedDB database.
 */
async function openDatabase(): Promise<IDBDatabase> {
  if (db) return db;

  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => {
      reject(new Error('Failed to open weather cache database'));
    };

    request.onsuccess = () => {
      db = request.result;
      resolve(db);
    };

    request.onupgradeneeded = (event) => {
      const database = (event.target as IDBOpenDBRequest).result;

      // Cached weather files store
      if (!database.objectStoreNames.contains(CACHE_STORE)) {
        const cacheStore = database.createObjectStore(CACHE_STORE, { keyPath: 'id' });
        cacheStore.createIndex('cachedAt', 'cachedAt', { unique: false });
        cacheStore.createIndex('lastAccessed', 'lastAccessed', { unique: false });
        cacheStore.createIndex('size', 'size', { unique: false });
      }

      // Recent files store
      if (!database.objectStoreNames.contains(RECENT_STORE)) {
        const recentStore = database.createObjectStore(RECENT_STORE, { keyPath: 'id' });
        recentStore.createIndex('lastUsed', 'lastUsed', { unique: false });
      }

      // Favorites store
      if (!database.objectStoreNames.contains(FAVORITES_STORE)) {
        database.createObjectStore(FAVORITES_STORE, { keyPath: 'id' });
      }

      // Preferences store
      if (!database.objectStoreNames.contains(PREFERENCES_STORE)) {
        database.createObjectStore(PREFERENCES_STORE, { keyPath: 'key' });
      }

      // DDY cache store (for on-demand fetched design day data)
      if (!database.objectStoreNames.contains(DDY_CACHE_STORE)) {
        const ddyStore = database.createObjectStore(DDY_CACHE_STORE, { keyPath: 'id' });
        ddyStore.createIndex('cachedAt', 'cachedAt', { unique: false });
      }

      // Search history store (for quick access to recent searches)
      if (!database.objectStoreNames.contains(SEARCH_HISTORY_STORE)) {
        const searchStore = database.createObjectStore(SEARCH_HISTORY_STORE, { keyPath: 'id' });
        searchStore.createIndex('timestamp', 'timestamp', { unique: false });
      }
    };
  });
}

/**
 * Get a cached weather file by ID.
 */
export async function getCachedWeatherFile(id: string): Promise<CachedWeatherFile | null> {
  const database = await openDatabase();

  return new Promise((resolve, reject) => {
    const transaction = database.transaction(CACHE_STORE, 'readwrite');
    const store = transaction.objectStore(CACHE_STORE);
    const request = store.get(id);

    request.onsuccess = () => {
      const result = request.result as CachedWeatherFile | undefined;
      if (result) {
        // Update last accessed time
        result.lastAccessed = new Date();
        result.accessCount++;
        const putRequest = store.put(result);
        // Log but don't fail if metadata update fails - the cached file is still valid
        putRequest.onerror = () => {
          console.warn('Failed to update weather file cache metadata:', putRequest.error);
        };
        resolve(result);
      } else {
        resolve(null);
      }
    };

    request.onerror = () => {
      reject(new Error('Failed to get cached weather file'));
    };
  });
}

/**
 * Cache a weather file.
 */
export async function cacheWeatherFile(
  id: string,
  content: string
): Promise<void> {
  const database = await openDatabase();
  const preferences = await getPreferences();

  // Check cache size limits
  await enforceeCacheLimits(preferences);

  return new Promise((resolve, reject) => {
    const transaction = database.transaction(CACHE_STORE, 'readwrite');
    const store = transaction.objectStore(CACHE_STORE);

    const cached: CachedWeatherFile = {
      id,
      content,
      size: new Blob([content]).size,
      cachedAt: new Date(),
      lastAccessed: new Date(),
      accessCount: 1,
    };

    const request = store.put(cached);

    request.onsuccess = () => resolve();
    request.onerror = () => reject(new Error('Failed to cache weather file'));
  });
}

/**
 * Remove a cached weather file.
 */
export async function removeCachedWeatherFile(id: string): Promise<void> {
  const database = await openDatabase();

  return new Promise((resolve, reject) => {
    const transaction = database.transaction(CACHE_STORE, 'readwrite');
    const store = transaction.objectStore(CACHE_STORE);
    const request = store.delete(id);

    request.onsuccess = () => resolve();
    request.onerror = () => reject(new Error('Failed to remove cached weather file'));
  });
}

/**
 * Get all cached file IDs and sizes.
 */
export async function getCacheInfo(): Promise<{
  files: Array<{ id: string; size: number; cachedAt: Date }>;
  totalSize: number;
}> {
  const database = await openDatabase();

  return new Promise((resolve, reject) => {
    const transaction = database.transaction(CACHE_STORE, 'readonly');
    const store = transaction.objectStore(CACHE_STORE);
    const request = store.getAll();

    request.onsuccess = () => {
      const files = (request.result as CachedWeatherFile[]).map((f) => ({
        id: f.id,
        size: f.size,
        cachedAt: f.cachedAt,
      }));
      const totalSize = files.reduce((sum, f) => sum + f.size, 0);
      resolve({ files, totalSize });
    };

    request.onerror = () => reject(new Error('Failed to get cache info'));
  });
}

/**
 * Clear all cached weather files.
 */
export async function clearCache(): Promise<void> {
  const database = await openDatabase();

  return new Promise((resolve, reject) => {
    const transaction = database.transaction(CACHE_STORE, 'readwrite');
    const store = transaction.objectStore(CACHE_STORE);
    const request = store.clear();

    request.onsuccess = () => resolve();
    request.onerror = () => reject(new Error('Failed to clear cache'));
  });
}

/**
 * Enforce cache size and file count limits.
 */
async function enforceeCacheLimits(preferences: WeatherLibraryPreferences): Promise<void> {
  await openDatabase();
  const { files, totalSize } = await getCacheInfo();

  const maxSizeBytes = preferences.maxCacheSize * 1024 * 1024; // MB to bytes
  const maxFiles = preferences.maxCachedFiles;

  // Check if we need to remove files
  let needsCleanup = totalSize > maxSizeBytes || files.length >= maxFiles;

  if (!needsCleanup) return;

  // Sort by last accessed (oldest first)
  const sortedFiles = files.sort(
    (a, b) => new Date(a.cachedAt).getTime() - new Date(b.cachedAt).getTime()
  );

  let currentSize = totalSize;
  let currentCount = files.length;

  for (const file of sortedFiles) {
    if (currentSize <= maxSizeBytes * 0.8 && currentCount < maxFiles) {
      break;
    }

    await removeCachedWeatherFile(file.id);
    currentSize -= file.size;
    currentCount--;
  }
}

// ============ Recent Files ============

/**
 * Add a file to recent files list.
 */
export async function addRecentFile(file: RecentWeatherFile): Promise<void> {
  const database = await openDatabase();
  const preferences = await getPreferences();

  return new Promise((resolve, reject) => {
    const transaction = database.transaction(RECENT_STORE, 'readwrite');
    const store = transaction.objectStore(RECENT_STORE);

    // Update or add
    file.lastUsed = new Date();
    const request = store.put(file);

    request.onsuccess = async () => {
      // Enforce limit
      await enforceRecentFilesLimit(preferences.recentFilesLimit);
      resolve();
    };
    request.onerror = () => reject(new Error('Failed to add recent file'));
  });
}

/**
 * Get recent files, sorted by most recent first.
 */
export async function getRecentFiles(): Promise<RecentWeatherFile[]> {
  const database = await openDatabase();

  return new Promise((resolve, reject) => {
    const transaction = database.transaction(RECENT_STORE, 'readonly');
    const store = transaction.objectStore(RECENT_STORE);
    const index = store.index('lastUsed');
    const request = index.getAll();

    request.onsuccess = () => {
      const files = request.result as RecentWeatherFile[];
      // Sort by most recent first
      files.sort((a, b) => new Date(b.lastUsed).getTime() - new Date(a.lastUsed).getTime());
      resolve(files);
    };

    request.onerror = () => reject(new Error('Failed to get recent files'));
  });
}

/**
 * Remove a recent file entry.
 */
export async function removeRecentFile(id: string): Promise<void> {
  const database = await openDatabase();

  return new Promise((resolve, reject) => {
    const transaction = database.transaction(RECENT_STORE, 'readwrite');
    const store = transaction.objectStore(RECENT_STORE);
    const request = store.delete(id);

    request.onsuccess = () => resolve();
    request.onerror = () => reject(new Error('Failed to remove recent file'));
  });
}

/**
 * Clear all recent files.
 */
export async function clearRecentFiles(): Promise<void> {
  const database = await openDatabase();

  return new Promise((resolve, reject) => {
    const transaction = database.transaction(RECENT_STORE, 'readwrite');
    const store = transaction.objectStore(RECENT_STORE);
    const request = store.clear();

    request.onsuccess = () => resolve();
    request.onerror = () => reject(new Error('Failed to clear recent files'));
  });
}

/**
 * Enforce recent files limit.
 */
async function enforceRecentFilesLimit(limit: number): Promise<void> {
  const files = await getRecentFiles();

  if (files.length <= limit) return;

  const toRemove = files.slice(limit);
  for (const file of toRemove) {
    await removeRecentFile(file.id);
  }
}

// ============ Favorites ============

/**
 * Add a file to favorites.
 */
export async function addFavorite(id: string, note?: string): Promise<void> {
  const database = await openDatabase();

  return new Promise((resolve, reject) => {
    const transaction = database.transaction(FAVORITES_STORE, 'readwrite');
    const store = transaction.objectStore(FAVORITES_STORE);

    const favorite: FavoriteWeatherFile = {
      id,
      addedAt: new Date(),
    };
    if (note !== undefined) {
      favorite.note = note;
    }

    const request = store.put(favorite);

    request.onsuccess = () => resolve();
    request.onerror = () => reject(new Error('Failed to add favorite'));
  });
}

/**
 * Remove a file from favorites.
 */
export async function removeFavorite(id: string): Promise<void> {
  const database = await openDatabase();

  return new Promise((resolve, reject) => {
    const transaction = database.transaction(FAVORITES_STORE, 'readwrite');
    const store = transaction.objectStore(FAVORITES_STORE);
    const request = store.delete(id);

    request.onsuccess = () => resolve();
    request.onerror = () => reject(new Error('Failed to remove favorite'));
  });
}

/**
 * Check if a file is favorited.
 */
export async function isFavorite(id: string): Promise<boolean> {
  const database = await openDatabase();

  return new Promise((resolve, reject) => {
    const transaction = database.transaction(FAVORITES_STORE, 'readonly');
    const store = transaction.objectStore(FAVORITES_STORE);
    const request = store.get(id);

    request.onsuccess = () => resolve(!!request.result);
    request.onerror = () => reject(new Error('Failed to check favorite'));
  });
}

/**
 * Get all favorite files.
 */
export async function getFavorites(): Promise<FavoriteWeatherFile[]> {
  const database = await openDatabase();

  return new Promise((resolve, reject) => {
    const transaction = database.transaction(FAVORITES_STORE, 'readonly');
    const store = transaction.objectStore(FAVORITES_STORE);
    const request = store.getAll();

    request.onsuccess = () => resolve(request.result as FavoriteWeatherFile[]);
    request.onerror = () => reject(new Error('Failed to get favorites'));
  });
}

// ============ Preferences ============

/**
 * Get weather library preferences.
 */
export async function getPreferences(): Promise<WeatherLibraryPreferences> {
  const database = await openDatabase();

  return new Promise((resolve, reject) => {
    const transaction = database.transaction(PREFERENCES_STORE, 'readonly');
    const store = transaction.objectStore(PREFERENCES_STORE);
    const request = store.get('preferences');

    request.onsuccess = () => {
      const result = request.result;
      // Return stored preferences, or defaults if none exist yet
      resolve(result?.value ?? { ...DEFAULT_WEATHER_LIBRARY_PREFERENCES });
    };

    request.onerror = () => {
      reject(new Error('Failed to get weather library preferences'));
    };
  });
}

/**
 * Save weather library preferences.
 */
export async function savePreferences(
  preferences: Partial<WeatherLibraryPreferences>
): Promise<void> {
  const database = await openDatabase();
  const current = await getPreferences();

  return new Promise((resolve, reject) => {
    const transaction = database.transaction(PREFERENCES_STORE, 'readwrite');
    const store = transaction.objectStore(PREFERENCES_STORE);

    const request = store.put({
      key: 'preferences',
      value: { ...current, ...preferences },
    });

    request.onsuccess = () => resolve();
    request.onerror = () => reject(new Error('Failed to save preferences'));
  });
}

// ============ DDY Cache ============

/**
 * Cached DDY data entry.
 */
interface CachedDdyData {
  id: string;
  data: string; // JSON stringified ParsedDesignDay[]
  cachedAt: Date;
}

/**
 * Get cached DDY data for a weather file.
 */
export async function getCachedDdyData(fileId: string): Promise<string | null> {
  const database = await openDatabase();

  return new Promise((resolve, reject) => {
    const transaction = database.transaction(DDY_CACHE_STORE, 'readonly');
    const store = transaction.objectStore(DDY_CACHE_STORE);
    const request = store.get(fileId);

    request.onsuccess = () => {
      const result = request.result as CachedDdyData | undefined;
      if (result) {
        resolve(result.data);
      } else {
        resolve(null);
      }
    };

    request.onerror = () => {
      reject(new Error('Failed to get cached DDY data'));
    };
  });
}

/**
 * Cache DDY data for a weather file.
 */
export async function cacheDdyData(fileId: string, data: string): Promise<void> {
  const database = await openDatabase();

  return new Promise((resolve, reject) => {
    const transaction = database.transaction(DDY_CACHE_STORE, 'readwrite');
    const store = transaction.objectStore(DDY_CACHE_STORE);

    const cached: CachedDdyData = {
      id: fileId,
      data,
      cachedAt: new Date(),
    };

    const request = store.put(cached);

    request.onsuccess = () => resolve();
    request.onerror = () => reject(new Error('Failed to cache DDY data'));
  });
}

/**
 * Remove cached DDY data for a weather file.
 */
export async function removeCachedDdyData(fileId: string): Promise<void> {
  const database = await openDatabase();

  return new Promise((resolve, reject) => {
    const transaction = database.transaction(DDY_CACHE_STORE, 'readwrite');
    const store = transaction.objectStore(DDY_CACHE_STORE);
    const request = store.delete(fileId);

    request.onsuccess = () => resolve();
    request.onerror = () => reject(new Error('Failed to remove cached DDY data'));
  });
}

/**
 * Clear all cached DDY data.
 */
export async function clearDdyCache(): Promise<void> {
  const database = await openDatabase();

  return new Promise((resolve, reject) => {
    const transaction = database.transaction(DDY_CACHE_STORE, 'readwrite');
    const store = transaction.objectStore(DDY_CACHE_STORE);
    const request = store.clear();

    request.onsuccess = () => resolve();
    request.onerror = () => reject(new Error('Failed to clear DDY cache'));
  });
}

// ============ Search History ============

const MAX_SEARCH_HISTORY = 20;

/**
 * Search history entry.
 */
export interface SearchHistoryEntry {
  /** Unique ID */
  id: string;
  /** Search query text */
  query: string;
  /** When the search was made */
  timestamp: Date;
  /** Number of results returned */
  resultCount: number;
  /** Selected filters when search was made */
  filters?: {
    country?: string;
    climateZones?: string[];
    sourceTypes?: string[];
    yearRanges?: string[];
  } | undefined;
}

/**
 * Add a search to history.
 */
export async function addSearchToHistory(entry: Omit<SearchHistoryEntry, 'id' | 'timestamp'>): Promise<void> {
  const database = await openDatabase();

  // Check if this exact search already exists and update it
  const existing = await findExistingSearch(entry.query, entry.filters);

  return new Promise((resolve, reject) => {
    const transaction = database.transaction(SEARCH_HISTORY_STORE, 'readwrite');
    const store = transaction.objectStore(SEARCH_HISTORY_STORE);

    const historyEntry: SearchHistoryEntry = {
      id: existing?.id ?? `search-${String(Date.now())}-${Math.random().toString(36).substring(2, 9)}`,
      query: entry.query,
      timestamp: new Date(),
      resultCount: entry.resultCount,
    };

    // Only add filters if defined
    if (entry.filters !== undefined) {
      historyEntry.filters = entry.filters;
    }

    const request = store.put(historyEntry);

    request.onsuccess = async () => {
      await enforceSearchHistoryLimit();
      resolve();
    };
    request.onerror = () => reject(new Error('Failed to add search to history'));
  });
}

/**
 * Find an existing search with the same query and filters.
 */
async function findExistingSearch(
  query: string,
  filters?: SearchHistoryEntry['filters']
): Promise<SearchHistoryEntry | null> {
  const history = await getSearchHistory();

  return history.find((entry) => {
    if (entry.query !== query) return false;

    // Compare filters
    const sameFilters = JSON.stringify(entry.filters || {}) === JSON.stringify(filters || {});
    return sameFilters;
  }) ?? null;
}

/**
 * Get search history, sorted by most recent first.
 */
export async function getSearchHistory(): Promise<SearchHistoryEntry[]> {
  const database = await openDatabase();

  return new Promise((resolve, reject) => {
    const transaction = database.transaction(SEARCH_HISTORY_STORE, 'readonly');
    const store = transaction.objectStore(SEARCH_HISTORY_STORE);
    const request = store.getAll();

    request.onsuccess = () => {
      const entries = request.result as SearchHistoryEntry[];
      // Sort by most recent first
      entries.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
      resolve(entries);
    };

    request.onerror = () => reject(new Error('Failed to get search history'));
  });
}

/**
 * Remove a search history entry.
 */
export async function removeSearchHistoryEntry(id: string): Promise<void> {
  const database = await openDatabase();

  return new Promise((resolve, reject) => {
    const transaction = database.transaction(SEARCH_HISTORY_STORE, 'readwrite');
    const store = transaction.objectStore(SEARCH_HISTORY_STORE);
    const request = store.delete(id);

    request.onsuccess = () => resolve();
    request.onerror = () => reject(new Error('Failed to remove search history entry'));
  });
}

/**
 * Clear all search history.
 */
export async function clearSearchHistory(): Promise<void> {
  const database = await openDatabase();

  return new Promise((resolve, reject) => {
    const transaction = database.transaction(SEARCH_HISTORY_STORE, 'readwrite');
    const store = transaction.objectStore(SEARCH_HISTORY_STORE);
    const request = store.clear();

    request.onsuccess = () => resolve();
    request.onerror = () => reject(new Error('Failed to clear search history'));
  });
}

/**
 * Enforce search history limit.
 */
async function enforceSearchHistoryLimit(): Promise<void> {
  const entries = await getSearchHistory();

  if (entries.length <= MAX_SEARCH_HISTORY) return;

  const toRemove = entries.slice(MAX_SEARCH_HISTORY);
  for (const entry of toRemove) {
    await removeSearchHistoryEntry(entry.id);
  }
}
